// Shared building blocks for the Special Sponsorship rosters. Each sponsorship category has its own
// roster component (SareeRoster, VahanaRoster, …); everything reused across them lives here so no single
// page file gets fat: the sponsor/pledge dialogs, the donor-mapping dialog, the email dialog, and the
// small formatting/label helpers.
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Alert, Button, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, FormControl, InputLabel, Select, MenuItem,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { get, post, patch } from '../../utils/api';

// --- formatting + label helpers ---
export const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' }); } catch { return String(iso || '').slice(0, 10); } };
export const fmtDateLong = (iso) => { try { return new Date(String(iso).length <= 10 ? `${String(iso).slice(0, 10)}T12:00:00` : iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' }); } catch { return String(iso || '').slice(0, 10); } };
export const escHtml = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const roleChip = (role, noun) => (role === 'daata'
  ? <Chip size="small" label={`${noun || 'Vahana'} Daata`} sx={{ bgcolor: 'primary.50', color: 'primary.dark' }} />
  : role === 'ubhaya_daata'
    ? <Chip size="small" label="Ubhaya Daata" color="warning" variant="outlined" />
    : <Chip size="small" label="role?" variant="outlined" />);
export const roleText = (role, noun) => (role === 'daata' ? `${noun || 'Vahana'} Daata` : role === 'ubhaya_daata' ? 'Ubhaya Daata' : '');

// Saree sponsorship is a 10-year term, INCLUSIVE of the pledge year: pledge 2026 → covers 2026–2035, so the
// last covered (expiry) year is pledge year + 9.
export const SPONSORSHIP_YEARS = 10;
export const expiresYear = (d) => { const y = parseInt(String(d || '').slice(0, 4), 10); return y ? y + SPONSORSHIP_YEARS - 1 : null; };
// Navaratri saree item names are "Day N (Morning|Evening) — <Alankaram>"; split into alankaram + session.
export const parseAlankaram = (name) => {
  const m = String(name || '').match(/^Day\s+(\d+)\s*\(\s*(morning|evening)\s*\)\s*[—–-]\s*(.+)$/i);
  if (!m) return { session: '', alankaram: String(name || '') };
  return { session: `Day ${m[1]} ${m[2].toLowerCase()}`, alankaram: m[3].trim() };
};

// Opens a new window and writes the given full-HTML document, then prints it. Shared by every roster's
// "Print roster". Returns an error string on failure (pop-up blocked), else ''.
export const printHtml = (html) => {
  const w = window.open('', '_blank');
  if (!w) return 'Allow pop-ups to print the roster.';
  w.document.open(); w.document.write(html); w.document.close();
  return '';
};

// --- dialogs ---
export function NoteDialog({ ctx, onSave, onClose }) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (ctx) setDraft(ctx.current || ''); }, [ctx]);
  const save = async () => { setSaving(true); try { await onSave(draft); } finally { setSaving(false); } };
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Note{ctx?.title ? ` — ${ctx.title}` : ''}</DialogTitle>
      <DialogContent dividers>
        <TextField autoFocus fullWidth multiline minRows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a note" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Search the devotee facet and map (link) a sponsor row to a devotee account.
export function MapDonorDialog({ ctx, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { setQ(''); setRows([]); }, [ctx]);
  useEffect(() => {
    if (!ctx || q.trim().length < 3) { setRows([]); return undefined; }
    let cancel = false; const t = setTimeout(async () => {
      setLoading(true);
      try { const res = await get('/ldb/facets/customers', { q: q.trim() }); if (!cancel) setRows(Array.isArray(res?.customers) ? res.customers : []); }
      catch { if (!cancel) setRows([]); } finally { if (!cancel) setLoading(false); }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, ctx]);
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Map donor{ctx ? ` — ${ctx.recognize_as}` : ''}</DialogTitle>
      <DialogContent dividers>
        <TextField autoFocus fullWidth size="small" placeholder="Search name or email (3+ chars)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ mb: 1.5 }} />
        {loading ? <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box> : (
          <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
            {rows.map((c) => (
              <Stack key={c.local_ref || c.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{`${c.first_name || ''} ${c.last_name || ''}`.trim() || '(no name)'}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.email || 'no email'}</Typography>
                </Box>
                <Button size="small" variant="contained" disabled={!c.local_ref} onClick={() => onPick(c.local_ref)}>Map</Button>
              </Stack>
            ))}
            {!loading && q.trim().length >= 3 && rows.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No matches.</Typography>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// Pledge details + payment history. Saree passes { customerId, variationId }; vahana passes
// { customerId, vahana, role }. ctx also carries { alankaram, sponsorName } for the header.
export function PledgeHistoryDialog({ ctx, slug, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!ctx) return undefined;
    let cancel = false; setLoading(true); setErr(''); setData(null);
    (async () => {
      try {
        const params = ctx.itemId
          ? { customer_id: ctx.customerId, item_id: ctx.itemId, role: ctx.role }
          : ctx.vahana
            ? { customer_id: ctx.customerId, vahana: ctx.vahana, role: ctx.role }
            : { customer_id: ctx.customerId, variation_id: ctx.variationId };
        const r = await get(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/pledge-history`, params);
        if (!cancel) setData(r);
      }
      catch (e) { if (!cancel) setErr(e?.message || 'Failed to load pledge history'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [ctx, slug]);
  const p = data?.pledge;
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pledge &amp; payment history{ctx ? ` — ${ctx.sponsorName}` : ''}</DialogTitle>
      <DialogContent dividers>
        {ctx && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{ctx.alankaram}</Typography>}
        {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          : err ? <Alert severity="error">{err}</Alert>
            : data ? (
              <>
                {p ? (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                    <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="flex-start">
                      <Box><Typography variant="caption" color="text.secondary">Pledge</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>#{p.order_number}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Amount</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>${Number(p.amount).toLocaleString('en-US')}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Date</Typography><Typography variant="body2">{fmtDate(`${p.date}T12:00:00`)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Status</Typography><Chip size="small" variant="outlined" color={p.status === 'pledge-pending' ? 'warning' : 'success'} label={p.status === 'pledge-pending' ? 'Pending' : 'Completed'} /></Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Chip size="small" color="success" variant="outlined" label={`Paid $${Number(data.paid_total).toLocaleString('en-US')}`} />
                      {data.balance > 0 && <Chip size="small" color="warning" variant="outlined" label={`Balance $${Number(data.balance).toLocaleString('en-US')}`} />}
                    </Stack>
                  </Paper>
                ) : <Alert severity="info" sx={{ mb: 2 }}>No pledge on record{(data.payments || []).length ? ' — the donations below have no pledge yet.' : ' for this sponsor.'}</Alert>}
                <Typography variant="overline" color="text.secondary">{(data.pledge) ? 'Payment history' : 'Donations'}</Typography>
                <Paper variant="outlined" sx={{ mt: 0.5 }}>
                  <Table size="small">
                    <TableHead><TableRow sx={{ '& th': { fontWeight: 600 } }}><TableCell>Date</TableCell><TableCell>Order</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                    <TableBody>
                      {(data.payments || []).length === 0 ? (
                        <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>No payments recorded.</TableCell></TableRow>
                      ) : data.payments.map((x) => (
                        <TableRow key={x.order_id} hover>
                          <TableCell>{fmtDate(`${x.date}T12:00:00`)}</TableCell>
                          <TableCell><MuiLink component={RouterLink} to={`/orders/${x.order_id}`}>#{x.order_number}</MuiLink></TableCell>
                          <TableCell align="right">${Number(x.amount).toLocaleString('en-US')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

export function EmailDialog({ open, slug, itemId, scopeLabel, myEmail, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancel = false;
    setPreview(null); setErr(''); setResult(null); setConfirming(false); setTestTo(myEmail || '');
    (async () => {
      setLoading(true);
      try { const res = await post(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/email/preview`, itemId ? { item_id: itemId } : {}); if (!cancel) setPreview(res); }
      catch (e) { if (!cancel) setErr(e?.message || 'Failed to build preview'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open, slug, itemId, myEmail]);

  const sendTest = async () => {
    if (!testTo.trim()) { setErr('Enter a test email'); return; }
    setBusy(true); setErr('');
    try { const r = await post(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/email/send`, { ...(itemId ? { item_id: itemId } : {}), test_to: testTo.trim() }); setResult({ test: true, ...r }); }
    catch (e) { setErr(e?.message || 'Test send failed'); } finally { setBusy(false); }
  };
  const sendAll = async () => {
    setBusy(true); setErr('');
    try { const r = await post(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/email/send`, itemId ? { item_id: itemId } : {}); setResult(r); setConfirming(false); }
    catch (e) { setErr(e?.message || 'Send failed'); } finally { setBusy(false); }
  };

  const sample = preview?.samples?.[0];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recognition email — {scopeLabel}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        {result && (
          <Alert severity={result.errors?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {result.test ? `Test sent to ${result.sent_to}.`
              : `Sent ${result.sent} email${result.sent === 1 ? '' : 's'}${result.skipped_count ? `, skipped ${result.skipped_count} with no email` : ''}${result.errors?.length ? `, ${result.errors.length} failed` : ''}.`}
          </Alert>
        )}
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box> : preview ? (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
              <Chip color="primary" label={`${preview.sendable} will receive`} />
              {preview.skipped?.length > 0 && (
                <Tooltip title={preview.skipped.map((s) => `${s.recognize_as} (${s.vahana})`).join(', ')}>
                  <Chip color="warning" variant="outlined" label={`${preview.skipped.length} skipped — no email`} />
                </Tooltip>
              )}
            </Stack>
            {sample ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Preview · {sample.recognize_as} ({sample.vahana}) → {sample.to}</Typography>
                <Typography variant="subtitle2" sx={{ mt: 0.5 }}>{sample.subject}</Typography>
                <Paper variant="outlined" sx={{ mt: 1, p: 1, bgcolor: 'grey.50', maxHeight: 360, overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: sample.html }} />
                </Paper>
              </Box>
            ) : <Typography color="text.secondary">No recipients with an email in this scope.</Typography>}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" label="Send a test to" value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={{ minWidth: 240 }} />
              <Button variant="outlined" onClick={sendTest} disabled={busy}>Send test</Button>
            </Box>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        {preview?.sendable > 0 && !confirming && <Button variant="contained" color="primary" disabled={busy} onClick={() => setConfirming(true)}>Send to {preview.sendable}</Button>}
        {confirming && <Button variant="contained" color="error" disabled={busy} onClick={sendAll}>{busy ? 'Sending…' : `Confirm — send ${preview.sendable} real emails`}</Button>}
      </DialogActions>
    </Dialog>
  );
}

export function AddSponsorDialog({ open, items, slug, noun = 'Item', onClose, onAdded }) {
  const [itemId, setItemId] = useState('');
  const [recognizeAs, setRecognizeAs] = useState('');
  const [role, setRole] = useState('daata');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (open) { setItemId(''); setRecognizeAs(''); setRole('daata'); setErr(''); } }, [open]);
  const submit = async () => {
    if (!itemId || !recognizeAs.trim()) { setErr(`${noun} and “recognize as” are required`); return; }
    setSaving(true); setErr('');
    try { await post(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/donors`, { item_id: itemId, recognize_as: recognizeAs.trim(), role }); await onAdded(); }
    catch (e) { setErr(e?.message || 'Failed to add'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add sponsor</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <FormControl size="small" fullWidth>
            <InputLabel>{noun}</InputLabel>
            <Select label={noun} value={itemId} onChange={(e) => setItemId(e.target.value)}>
              {items.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Recognize as (family wording)" value={recognizeAs} onChange={(e) => setRecognizeAs(e.target.value)} fullWidth />
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="daata">Daata</MenuItem>
              <MenuItem value="ubhaya_daata">Ubhaya Daata</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Set/override the recognize-as wording for a derived (pledge/order) sponsor. ctx carries
// { item_id, local_ref, role, recognize_as, vahana }.
export function RecognitionDialog({ ctx, slug, onClose, onSaved }) {
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (ctx) { setVal(ctx.recognize_as || ''); setErr(''); } }, [ctx]);
  const save = async () => {
    if (!ctx?.local_ref) { setErr('This sponsor has no linked devotee.'); return; }
    setSaving(true); setErr('');
    try {
      await post(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/recognition`,
        { item_id: ctx.item_id, customer_local_ref: ctx.local_ref, role: ctx.role, recognize_as: val.trim() });
      await onSaved(); onClose();
    } catch (e) { setErr(e?.message || 'Failed to save'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Recognition wording{ctx?.vahana ? ` — ${ctx.vahana}` : ''}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          How this family is recognized in the stage announcement and recognition email (e.g. “Kalyani and Ravi Srinivas Family”). Overrides the devotee's name.
        </Typography>
        <TextField autoFocus fullWidth value={val} onChange={(e) => setVal(e.target.value)} placeholder="Family recognition wording" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Thin shared page header (title + description + action buttons slot).
export function RosterHeader({ program, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
      <Box>
        <Typography variant="overline" color="text.secondary">Special sponsorships</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{program?.name || 'Sponsors'}</Typography>
        {program?.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>{program.description}</Typography>}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap">{children}</Stack>
    </Stack>
  );
}

// Linked-event banner shown above every roster.
export function LinkedEventBanner({ event }) {
  if (!event) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <Box>
        <Typography variant="overline" color="text.secondary">Linked event</Typography>
        <Typography sx={{ fontWeight: 600 }}>{event.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {fmtDate(event.event_date)}{event.event_end_date ? ` – ${fmtDate(event.event_end_date)}` : ''} · managed in Calendar Events
        </Typography>
      </Box>
    </Paper>
  );
}

export { patch };
